import { useMemo, useRef, useEffect } from 'react';
import { UnifiedMessage } from '../types/message';
import { separateIframeFromText } from '../utils/iframe';

export interface ProcessedMessage extends UnifiedMessage {
  displayContent: string;
  iframeContent?: string;
  hasIframe: boolean;
}

export interface UseMessageProcessorOptions {
  separateIframes?: boolean;
  autoScroll?: boolean;
}

export function useMessageProcessor(
  messages: UnifiedMessage[],
  options: UseMessageProcessorOptions = {}
) {
  const {
    separateIframes = true,
    autoScroll = true,
  } = options;
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Process messages: separate iframes if enabled
  const processedMessages = useMemo<ProcessedMessage[]>(() => {
    return messages.map(message => {
      if (separateIframes && message.from === 'assistant') {
        const { textContent, iframes, hasIframe } = separateIframeFromText(message.content);
        
        if (hasIframe) {
          return {
            ...message,
            displayContent: textContent,
            iframeContent: iframes.join('\n'),
            hasIframe: true,
          };
        }
      }
      
      return {
        ...message,
        displayContent: message.content,
        hasIframe: false,
      };
    });
  }, [messages, separateIframes]);
  
  // Auto-scroll effect
  useEffect(() => {
    if (autoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages, autoScroll]);
  
  // Filter to iframe-containing messages
  const iframeMessages = useMemo(() => {
    return processedMessages.filter(m => 
      m.hasIframe && 
      m.id !== -1 && // Exclude temp messages
      typeof m.id === 'number' // Exclude local state messages
    );
  }, [processedMessages]);
  
  return {
    processedMessages,
    iframeMessages,
    messagesEndRef,
  };
}

